import { useCallback, useEffect, useState } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { TooltipProvider } from "@cloch-ai/ui";
import { useAuth } from "@clerk/clerk-react";
import { ClerkProvider, isClerkConfigured } from "./providers/ClerkProvider";
import { OrgProvider, useCurrentOrgId } from "./providers/OrgProvider";
import { RoleProvider } from "./providers/RoleProvider";
import { GatewayProvider, useGateway } from "./providers/GatewayProvider";
import { useGatewayStore } from "./stores/gateway-store";
import { Routes } from "./routes";
import type { GatewayHelloOk, GatewayEventFrame } from "./gateway";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60,
      retry: 1,
    },
  },
});

/**
 * Gateway connection with Clerk auth.
 * Only used when Clerk is configured.
 */
function GatewayConnectionWithClerk({ children }: { children: React.ReactNode }) {
  const { gatewayUrl, token, setStatus, setHello } = useGatewayStore();
  const handleEvent = useGatewayStore((s) => s.handleEvent);
  const { getToken, isSignedIn } = useAuth();
  const currentOrgId = useCurrentOrgId();
  const [clerkJwt, setClerkJwt] = useState<string | undefined>();

  const resolveClerkJwt = useCallback(async () => {
    if (!isSignedIn) {
      return undefined;
    }
    try {
      const jwt = await getToken({ template: "command-session" });
      return jwt ?? undefined;
    } catch {
      return undefined;
    }
  }, [getToken, isSignedIn]);

  // Get Clerk JWT when signed in
  useEffect(() => {
    if (!isSignedIn) {
      setClerkJwt(undefined);
      return;
    }
    resolveClerkJwt().then((jwt) => setClerkJwt(jwt ?? undefined));
  }, [isSignedIn, currentOrgId, resolveClerkJwt]);

  return (
    <GatewayProvider
      url={gatewayUrl}
      token={token}
      clerkJwt={clerkJwt}
      resolveClerkJwt={resolveClerkJwt}
      orgId={currentOrgId}
      requireClerkJwt
    >
      <GatewaySync onStatusChange={setStatus} onHello={setHello} onEvent={handleEvent} />
      {children}
    </GatewayProvider>
  );
}

/**
 * Gateway connection without Clerk auth.
 * Used in development mode when Clerk is not configured.
 */
function GatewayConnectionDevMode({ children }: { children: React.ReactNode }) {
  const { gatewayUrl, token, setStatus, setHello } = useGatewayStore();
  const handleEvent = useGatewayStore((s) => s.handleEvent);

  return (
    <GatewayProvider url={gatewayUrl} token={token}>
      <GatewaySync onStatusChange={setStatus} onHello={setHello} onEvent={handleEvent} />
      {children}
    </GatewayProvider>
  );
}

/**
 * Gateway connection wrapper that handles both Clerk and dev mode.
 */
function GatewayConnection({ children }: { children: React.ReactNode }) {
  if (!isClerkConfigured()) {
    return <GatewayConnectionDevMode>{children}</GatewayConnectionDevMode>;
  }
  return <GatewayConnectionWithClerk>{children}</GatewayConnectionWithClerk>;
}

function GatewaySync({
  onStatusChange,
  onHello,
  onEvent,
}: {
  onStatusChange: (status: "disconnected" | "connecting" | "connected" | "error") => void;
  onHello: (hello: GatewayHelloOk | null) => void;
  onEvent: (event: GatewayEventFrame) => void;
}) {
  const { status, hello, lastEvent } = useGateway();

  useEffect(() => {
    onStatusChange(status);
  }, [status, onStatusChange]);

  useEffect(() => {
    onHello(hello);
  }, [hello, onHello]);

  useEffect(() => {
    if (lastEvent) {
      onEvent(lastEvent);
    }
  }, [lastEvent, onEvent]);

  return null;
}

/**
 * Root application component.
 *
 * Provider hierarchy:
 * ClerkProvider > QueryClientProvider > OrgProvider > RoleProvider > TooltipProvider > GatewayProvider
 *
 * - ClerkProvider wraps the entire app for authentication
 * - QueryClientProvider for React Query caching
 * - OrgProvider for multi-org context (needs QueryClient for cache invalidation)
 * - RoleProvider for role-based filtering (depends on OrgProvider for current org)
 * - TooltipProvider for UI tooltips
 * - GatewayProvider for WebSocket connection
 *
 * In development without VITE_CLERK_PUBLISHABLE_KEY, auth is bypassed.
 */
export default function App() {
  return (
    <ClerkProvider>
      <QueryClientProvider client={queryClient}>
        <OrgProvider>
          <RoleProvider>
            <TooltipProvider>
              <GatewayConnection>
                <Routes />
              </GatewayConnection>
            </TooltipProvider>
          </RoleProvider>
        </OrgProvider>
      </QueryClientProvider>
    </ClerkProvider>
  );
}
